import React from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ShoppingBag, Plane, CheckCircle, DollarSign, FileText, Shield, Clock, Zap, Bot, TrendingUp } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/auth';

const Features = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const handleGetStarted = (feature: 'refund' | 'flight') => {
    if (user) {
      navigate(feature === 'refund' ? '/refund' : '/pricing');
    } else {
      navigate('/auth', { state: { defaultTab: 'signup' } });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5">
      <div className="container mx-auto px-4 py-16">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-6">
            Get Your Money Back Easily
          </h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            AI-powered tools to help you recover money from online purchases and flight disruptions.
          </p>
        </div>

        {/* Feature Cards */}
        <div className="grid md:grid-cols-2 gap-8 mb-16">
          {/* Online Shopping Refunds Feature */}
          <Card className="relative overflow-hidden border-2 hover:border-primary/20 transition-all duration-300 group">
            <CardHeader className="pb-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-3 bg-primary/10 rounded-xl">
                  <ShoppingBag className="h-8 w-8 text-primary" />
                </div>
              </div>
              <CardTitle className="text-2xl mb-2">Online Shopping Refunds</CardTitle>
              <CardDescription className="text-base">
                Get your money back from any online purchase. AI generates professional refund requests and chargeback reports.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-3">
                <div className="flex items-center gap-3">
                  <Bot className="h-5 w-5 text-primary flex-shrink-0" />
                  <span className="text-sm">AI-powered refund letters</span>
                </div>
                <div className="flex items-center gap-3">
                  <FileText className="h-5 w-5 text-primary flex-shrink-0" />
                  <span className="text-sm">Bank chargeback reports</span>
                </div>
                <div className="flex items-center gap-3">
                  <Shield className="h-5 w-5 text-primary flex-shrink-0" />
                  <span className="text-sm">Consumer rights protection</span>
                </div>
              </div>

              <Button 
                className="w-full mt-6" 
                onClick={() => handleGetStarted('refund')}
              >
                <ShoppingBag className="h-4 w-4 mr-2" />
                Start Shopping Claim
              </Button>
            </CardContent>
          </Card>

          {/* Flight Claims Feature */}
          <Card className="relative overflow-hidden border-2 hover:border-primary/20 transition-all duration-300 group">
            <CardHeader className="pb-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-3 bg-blue-100 rounded-xl">
                  <Plane className="h-8 w-8 text-blue-600" />
                </div>
              </div>
              <CardTitle className="text-2xl mb-2">Flight Compensation Claims</CardTitle>
              <CardDescription className="text-base">
                Get compensation for flight delays and cancellations. Covers EU261, U.S. DOT rules, and the Montreal Convention for international flights.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-3">
                <div className="flex items-center gap-3">
                  <CheckCircle className="h-5 w-5 text-blue-600 flex-shrink-0" />
                  <span className="text-sm">Free eligibility check</span>
                </div>
                <div className="flex items-center gap-3">
                  <DollarSign className="h-5 w-5 text-blue-600 flex-shrink-0" />
                  <span className="text-sm">Up to €600 compensation</span>
                </div>
                <div className="flex items-center gap-3">
                  <Zap className="h-5 w-5 text-blue-600 flex-shrink-0" />
                  <span className="text-sm">AI-generated claim letters</span>
                </div>
              </div>

              <Button 
                variant="outline" 
                className="w-full mt-6 border-blue-600 text-blue-600 hover:bg-blue-50" 
                onClick={() => handleGetStarted('flight')}
              >
                <Plane className="h-4 w-4 mr-2" />
                Check Flight Eligibility
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* CTA Section */}
        <div className="text-center bg-primary/5 rounded-2xl p-8 border">
          <h2 className="text-2xl font-bold mb-4">Ready to Get Your Money Back?</h2>
          <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
            Join thousands of satisfied customers who have successfully recovered their money using SnapRefund's AI-powered platform.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" onClick={() => handleGetStarted('refund')}>
              Start Your First Claim
            </Button>
            <Button size="lg" variant="outline" onClick={() => navigate('/pricing')}>
              View Pricing
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Features;